import { useState, useCallback, useEffect, useRef } from 'react';
import type { CreativeAngle, UGCScript, CreativeReview, Brief } from '../types';

interface DerivativeEnvelope<T> {
  data: T;
  source_brief_version: number;
  generated_at?: string;
}

interface UseDerivativesReturn {
  angles: CreativeAngle[];
  anglesStale: boolean;
  anglesGenerating: boolean;
  setAngles: (angles: CreativeAngle[], briefVersion: number) => void;
  generateAngles: (brief: Brief, apiBase: string) => Promise<void>;
  script: UGCScript | null;
  scriptStale: boolean;
  scriptGenerating: boolean;
  setScript: (script: UGCScript | null, briefVersion: number) => void;
  generateScript: (brief: Brief, angle: CreativeAngle, apiBase: string) => Promise<void>;
  review: CreativeReview | null;
  reviewStale: boolean;
  setReview: (review: CreativeReview | null, briefVersion: number) => void;
  generationError: string | null;
}

async function apiFetch(url: string, method = 'GET', body?: unknown) {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

export function useDerivatives(currentBriefVersion: number, brief: Brief | null): UseDerivativesReturn {
  const [anglesState, setAnglesState] = useState<DerivativeEnvelope<CreativeAngle[]>>({
    data: [],
    source_brief_version: 0,
  });
  const [scriptState, setScriptState] = useState<DerivativeEnvelope<UGCScript | null>>({
    data: null,
    source_brief_version: 0,
  });
  const [reviewState, setReviewState] = useState<DerivativeEnvelope<CreativeReview | null>>({
    data: null,
    source_brief_version: 0,
  });

  const [anglesGenerating, setAnglesGenerating] = useState(false);
  const [scriptGenerating, setScriptGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const lastSyncedAt = useRef<string | null>(null);

  // Sync derivatives from brief.metadata.derivatives when the brief changes
  useEffect(() => {
    if (!brief) return;
    const derivatives = (brief.metadata as Record<string, unknown> | undefined)?.derivatives as Record<string, DerivativeEnvelope<unknown>> | undefined;
    if (!derivatives) return;

    const syncKey = JSON.stringify({
      a: derivatives.angles?.generated_at,
      s: derivatives.script?.generated_at,
      r: derivatives.review?.generated_at,
    });
    if (syncKey === lastSyncedAt.current) return;
    lastSyncedAt.current = syncKey;

    if (derivatives.angles?.data) {
      const anglesData = derivatives.angles.data as CreativeAngle[];
      if (Array.isArray(anglesData) && anglesData.length > 0) {
        setAnglesState({
          data: anglesData,
          source_brief_version: (derivatives.angles.source_brief_version as number) ?? 0,
        });
      }
    }

    if (derivatives.script?.data) {
      setScriptState({
        data: derivatives.script.data as UGCScript,
        source_brief_version: (derivatives.script.source_brief_version as number) ?? 0,
      });
    }

    if (derivatives.review?.data) {
      setReviewState({
        data: derivatives.review.data as CreativeReview,
        source_brief_version: (derivatives.review.source_brief_version as number) ?? 0,
      });
    }
  }, [brief]);

  const setAngles = useCallback((angles: CreativeAngle[], briefVersion: number) => {
    setAnglesState({ data: angles, source_brief_version: briefVersion });
  }, []);

  const setScript = useCallback((script: UGCScript | null, briefVersion: number) => {
    setScriptState({ data: script, source_brief_version: briefVersion });
  }, []);

  const setReview = useCallback((review: CreativeReview | null, briefVersion: number) => {
    setReviewState({ data: review, source_brief_version: briefVersion });
  }, []);

  const generateAngles = useCallback(async (b: Brief, apiBase: string) => {
    setAnglesGenerating(true);
    setGenerationError(null);
    try {
      const creativeBrief = {
        client_uid: b.client_uid,
        persona_id: b.persona_id,
        version: b.version,
        ...(b.audience ?? {}),
        ...(b.brand_voice ?? {}),
        ...(b.insight ?? {}),
      };
      const productBrief = b.product_brief ?? {};
      const result = await apiFetch(`${apiBase}/api/v1/skills/invoke/angles`, 'POST', {
        creative_brief: creativeBrief,
        product_brief: productBrief,
        num_angles: 5,
        client_uid: b.client_uid,
      });

      const angles = result?.output?.angles ?? result?.angles ?? [];
      if (angles.length > 0) {
        setAnglesState({ data: angles, source_brief_version: b.version ?? 0 });

        // Persist to brief metadata
        try {
          const briefRes = await apiFetch(`${apiBase}/api/v1/generation/briefs/${b.brief_id}`);
          const existingMeta = briefRes.metadata ?? {};
          const existingDerivatives = existingMeta.derivatives ?? {};
          existingDerivatives.angles = {
            data: angles,
            source_brief_version: b.version ?? 0,
            generated_at: new Date().toISOString(),
          };
          await apiFetch(`${apiBase}/api/v1/generation/briefs/${b.brief_id}`, 'PATCH', {
            metadata: { ...existingMeta, derivatives: existingDerivatives },
          });
        } catch {
          // Persistence failure is non-critical
        }
      }
    } catch (e) {
      setGenerationError(e instanceof Error ? e.message : 'Failed to generate angles');
    } finally {
      setAnglesGenerating(false);
    }
  }, []);

  const generateScript = useCallback(async (b: Brief, angle: CreativeAngle, apiBase: string) => {
    setScriptGenerating(true);
    setGenerationError(null);
    try {
      const creativeBrief = {
        client_uid: b.client_uid,
        persona_id: b.persona_id,
        version: b.version,
        ...(b.audience ?? {}),
        ...(b.brand_voice ?? {}),
        ...(b.insight ?? {}),
      };
      const result = await apiFetch(`${apiBase}/api/v1/skills/invoke/script`, 'POST', {
        angle,
        creative_brief: creativeBrief,
        framework_id: 'hook_body_cta',
        target_duration: 30,
        platform: 'tiktok',
      });

      const script = result?.output ?? result?.script ?? result;
      if (script) {
        setScriptState({ data: script, source_brief_version: b.version ?? 0 });

        try {
          const briefRes = await apiFetch(`${apiBase}/api/v1/generation/briefs/${b.brief_id}`);
          const existingMeta = briefRes.metadata ?? {};
          const existingDerivatives = existingMeta.derivatives ?? {};
          existingDerivatives.script = {
            data: script,
            source_brief_version: b.version ?? 0,
            source_angle_id: angle.angle_id,
            generated_at: new Date().toISOString(),
          };
          await apiFetch(`${apiBase}/api/v1/generation/briefs/${b.brief_id}`, 'PATCH', {
            metadata: { ...existingMeta, derivatives: existingDerivatives },
          });
        } catch {
          // non-critical
        }
      }
    } catch (e) {
      setGenerationError(e instanceof Error ? e.message : 'Failed to generate script');
    } finally {
      setScriptGenerating(false);
    }
  }, []);

  return {
    angles: anglesState.data,
    anglesStale: anglesState.data.length > 0 && anglesState.source_brief_version < currentBriefVersion,
    anglesGenerating,
    setAngles,
    generateAngles,
    script: scriptState.data,
    scriptStale: scriptState.data !== null && scriptState.source_brief_version < currentBriefVersion,
    scriptGenerating,
    setScript,
    generateScript,
    review: reviewState.data,
    reviewStale: reviewState.data !== null && reviewState.source_brief_version < currentBriefVersion,
    setReview,
    generationError,
  };
}
